import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, ChevronRight, History } from 'lucide-react'
import {
  useListOccurrenceInvestigationsQuery,
  type WorkScope
} from '@axilon-apps/domains-ontology-ui-state'
import { runPageHref } from '../../lib/component-links'
import { formatCompactLocalTime } from '../../lib/time'
import { runStatusView } from '../../pages/RunPage/run-status'
import { StatusPill } from '../primitives/StatusPill'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible'
import { WorkError } from './OccurrenceTasks'

export function OccurrenceRuns({
  scope,
  id,
  action
}: {
  scope: WorkScope
  id: string
  action?: ReactNode
}) {
  const [showAll, setShowAll] = useState(false)
  const query = useListOccurrenceInvestigationsQuery(
    { ...scope, id },
    { refetchOnMountOrArgChange: true }
  )
  const runs = query.isError
    ? undefined
    : query.currentData &&
      [...query.currentData].sort(
        (a, b) =>
          Date.parse(b.created_at ?? b.linked_at) - Date.parse(a.created_at ?? a.linked_at) ||
          b.run_id.localeCompare(a.run_id)
      )
  const visibleRuns = showAll ? runs : runs?.slice(0, 3)
  return (
    <Collapsible asChild>
      <section aria-label="Run history" className="mt-2">
        <div className="flex items-center justify-between gap-3">
          <CollapsibleTrigger className="group inline-flex items-center gap-1.5 rounded py-1 text-12 font-medium text-ink-3 outline-none transition-colors hover:text-brand focus-visible:ring-2 focus-visible:ring-brand">
            <History size={14} aria-hidden />
            <span className="whitespace-nowrap tabular-nums group-hover:underline">
              Run history{runs && ` (${runs.length})`}
            </span>
            <ChevronRight
              size={12}
              aria-hidden
              className="transition-transform group-data-[state=open]:rotate-90"
            />
          </CollapsibleTrigger>
          {action}
        </div>
        <CollapsibleContent className="pt-3">
          {query.isError ? (
            <WorkError message="Couldn't load runs." retry={() => void query.refetch()} />
          ) : !visibleRuns ? (
            <p role="status" className="m-0 py-2 text-13 text-ink-3">
              Loading runs…
            </p>
          ) : visibleRuns.length === 0 ? (
            <p className="m-0 py-2 text-13 text-ink-3">No runs linked yet.</p>
          ) : (
            <ol className="m-0 max-h-80 list-none overflow-y-auto py-1 pl-2 pr-1">
              {visibleRuns.map((run) => {
                const status = runStatusView(run.status ?? '')
                const time = run.created_at ?? run.linked_at
                return (
                  <li
                    key={run.run_id}
                    className="relative border-l border-line pb-3 pl-4 last:border-transparent last:pb-0"
                  >
                    <span
                      aria-hidden
                      className="absolute -left-[3.5px] top-2 size-1.5 rounded-full bg-ink-4 ring-4 ring-bg-elev"
                    />
                    <Link
                      to={runPageHref('custom', run.run_id)}
                      className="group flex min-w-0 items-start gap-3 rounded px-1 py-0.5 outline-none transition-colors hover:bg-bg-sunk/45 focus-visible:ring-2 focus-visible:ring-brand"
                    >
                      <span className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="break-words text-13 font-medium text-ink group-hover:text-brand">
                          {run.agent_name?.trim() || 'Agent run'}
                        </span>
                        <time dateTime={time} className="text-12 text-ink-3">
                          {formatCompactLocalTime(time)}
                        </time>
                      </span>
                      {run.status && (
                        <StatusPill status={status.tone} withDot={false} className="shrink-0">
                          {status.label}
                        </StatusPill>
                      )}
                      <ArrowUpRight size={14} aria-hidden className="mt-0.5 shrink-0 text-ink-3" />
                    </Link>
                  </li>
                )
              })}
            </ol>
          )}
          {runs && runs.length > 3 && (
            <button
              type="button"
              aria-expanded={showAll}
              onClick={() => setShowAll((previous) => !previous)}
              className="ml-6 mt-2 rounded px-1 py-1 text-12 font-medium text-brand outline-none hover:underline focus-visible:ring-2 focus-visible:ring-brand"
            >
              {showAll ? 'Show fewer runs' : `Show all ${runs.length} runs`}
            </button>
          )}
        </CollapsibleContent>
      </section>
    </Collapsible>
  )
}
